import CalendarRange from "@components/CalendarRange";
import { CheckListIcon } from "@components/Icons";
import { useUserState } from "@context/UserContext";
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { ExpandMoreOutlined } from "@material-ui/icons";
import { formatedLegalName, maskCNPJ } from "@utils/index";
import { useFormikContext } from "formik";
import React, { useMemo } from "react";
import { useFormQueryInvoices } from "./FormQueryInvoicesContext";
import { IQuery } from "./interface";
import { useStyles } from "./styles";

const FormQuery: React.FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only("xs"));
  const {
    values,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext<IQuery>();
  const formQueryInvoicesContext = useFormQueryInvoices();
  const userState = useUserState();

  const isDateRangeValid = (
    dateRange: [Date | null, Date | null] | null | undefined
  ): boolean => {
    return !!dateRange && !!dateRange[0] && !!dateRange[1];
  };

  const enableButton = useMemo((): boolean => {
    return values.CNPJ.length === 0 || !isDateRangeValid(values.dateRange);
  }, [values.CNPJ.length, values.dateRange]);

  const renderCNPJ = () => {
    return userState.listCNPJ.map((ele, idx) => {
      return (
        <MenuItem
          key={ele.BpID ? ele.BpID : ele.CNPJ}
          value={ele.BpID ? ele.BpID : ele.CNPJ}
          className={classes.itemSelect}
        >
          <Grid container>
            <Grid item>
              <Checkbox
                style={{
                  color: "#D91F05",
                  width: isMobile ? "10px" : "",
                  paddingLeft: isMobile ? "5px" : "",
                  marginRight: isMobile ? "10px" : "",
                  fontSize: "10px",
                }}
                color="primary"
                checked={
                  ele.BpID
                    ? values.CNPJ.indexOf(ele.BpID) > -1
                    : values.CNPJ.indexOf(ele.CNPJ) > -1
                }
              />
            </Grid>
            <Grid item>
              <Typography
                className={classes.itemSelectTitle}
                style={{ fontSize: isMobile ? "12px" : "" }}
              >
                {formatedLegalName(ele.companyName)}
              </Typography>
              <Typography className={classes.itemSelectSubTitle}>
                {maskCNPJ(ele.CNPJ)}
              </Typography>
              {ele.BpID && (
                <Typography className={classes.itemSelectSubTitle}>
                  BP: {ele.BpID}
                </Typography>
              )}
            </Grid>
          </Grid>
        </MenuItem>
      );
    });
  };

  return (
    <Grid
      container
      alignItems="center"
      spacing={2}
      style={{
        backgroundColor: "#FFF",
        padding: "2rem",
        borderRadius: "4px",
      }}
    >
      <Grid
        item
        xs={12}
        md={12}
        lg={12}
        style={{ display: "flex", justifyContent: "space-between" }}
      >
        <Typography
          style={{ fontSize: isMobile ? "1.5rem" : "2rem", fontWeight: 700 }}
        >
          Consultar notas fiscais
        </Typography>
        <CheckListIcon />
      </Grid>

      <Grid item xs={isMobile ? 12 : 6}>
        <FormControl variant="outlined" fullWidth>
          <InputLabel style={{ color: "#3E3D3D", fontWeight: 700 }}>
            Razão social
          </InputLabel>
          <Select
            label="Razão social"
            className={classes.customSelect}
            style={{
              paddingLeft: isMobile ? "0px" : "",
            }}
            error={!!errors.CNPJ && !!touched.CNPJ}
            onBlur={() => setFieldTouched("CNPJ", true)}
            value={values.CNPJ}
            IconComponent={ExpandMoreOutlined}
            onChange={({ target }) => {
              setFieldValue("CNPJ", target.value);
            }}
            name="CNPJ"
            MenuProps={{
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left",
              },
              getContentAnchorEl: null,
            }}
            multiple
            renderValue={(selected) => (
              <div
                style={{
                  display: "flex",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {(selected as string[]).map((value) => {
                  return (
                    <Typography
                      style={{
                        marginLeft: "0px",
                        fontSize: isMobile ? "12px" : "",
                      }}
                    >
                      {
                        userState.listCNPJ.find((CNPJ) =>
                          CNPJ.BpID ? CNPJ.BpID === value : CNPJ.CNPJ === value
                        )?.companyName
                      }
                      {(selected as string[]).length > 1 && " / "}
                    </Typography>
                  );
                })}
              </div>
            )}
          >
            {renderCNPJ()}
          </Select>
          <FormHelperText error={!!errors.CNPJ && !!touched.CNPJ}>
            {!!touched.CNPJ && errors.CNPJ}
          </FormHelperText>
        </FormControl>
      </Grid>

      <Grid item xs={isMobile ? 12 : 3}>
        <CalendarRange
          name="dateRange"
          label="Período de emissão"
          value={values.dateRange}
          onChange={(dateRange) => {
            setFieldValue("dateRange", dateRange);
            setFieldTouched("beginDate", true);
            setFieldTouched("endDate", true);
          }}
          onBlur={() => {
            setFieldTouched("beginDate", true);
            setFieldTouched("endDate", true);
          }}
        />
      </Grid>

      <Grid
        item
        container
        style={{ marginTop: "2rem" }}
        justify="flex-end"
        xs={12}
      >
        <Button
          disabled={enableButton}
          style={{ boxShadow: "none", textTransform: "capitalize" }}
          color="primary"
          type="submit"
          variant="contained"
          startIcon={
            formQueryInvoicesContext.loadingQuery && (
              <CircularProgress
                color="secondary"
                style={{ height: 20, width: 20 }}
              />
            )
          }
        >
          Consultar
        </Button>
      </Grid>
    </Grid>
  );
};

export default FormQuery;
